import React, { useState, useEffect, useRef } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import SEOMetaTags from '../components/SEOMetaTags';
import Header from '../components/Header';
import { trackPageView } from '../analytics';

const ChatWithPdf = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({ type: null, message: null });
  const [uploadedFileId, setUploadedFileId] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState(null);
  
  // Reference to the chat messages container for auto-scrolling
  const chatContainerRef = useRef(null);
  
  // Chat state
  const [question, setQuestion] = useState('');
  const [isQuerying, setIsQuerying] = useState(false);
  const [messages, setMessages] = useState([
    {
      type: 'assistant',
      content: 'Hello! I\'m your AI assistant. Upload a PDF and ask me anything about it.'
    }
  ]);
  
  // Maximum file size in bytes
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  
  // Track page view
  useEffect(() => {
    if (typeof window !== 'undefined') {
      trackPageView(window.location.pathname);
    }
  }, []);

  // Auto-scroll to the latest message when messages change
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages, isQuerying]);
  
  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    
    // Reset states
    setUploadStatus({ type: null, message: null });
    setSelectedFile(null);
    
    // Validate file type
    if (file && !file.type.includes('pdf')) {
      setUploadStatus({
        type: 'error',
        message: 'Please select a PDF file.'
      });
      return;
    }
    
    // Validate file size
    if (file && file.size > MAX_FILE_SIZE) {
      setUploadStatus({
        type: 'error',
        message: 'File size exceeds 5MB. Please select a smaller file.'
      });
      return;
    }
    
    setSelectedFile(file);
  };
  
  // Handle PDF download
  const handleDownloadPDF = async () => {
    if (messages.length <= 1) {
      setUploadStatus({
        type: 'warning',
        message: 'No conversation to download yet. Start chatting first!'
      });
      return;
    }

    try {
      // Get the width of the chat container for proper rendering
      const chatWidth = chatContainerRef.current?.offsetWidth || 800;
      
      // Create a temporary div to render the conversation
      const pdfContent = document.createElement('div');
      
      // Position far off-screen but still rendered (for html2canvas to work)
      // Using large negative values to ensure it's completely off-screen
      pdfContent.style.position = 'absolute';
      pdfContent.style.left = '-9999px';
      pdfContent.style.top = '-9999px';
      pdfContent.style.width = `${chatWidth}px`;
      // Don't use opacity:0 as it might affect rendering
      pdfContent.style.pointerEvents = 'none';
      pdfContent.style.zIndex = '-9999';
      pdfContent.style.padding = '20px';
      pdfContent.style.fontFamily = 'Arial, sans-serif';
      pdfContent.style.backgroundColor = 'white';
      // Ensure the element is actually rendered
      pdfContent.style.overflow = 'visible';
      
      // Add title
      const title = document.createElement('h1');
      title.textContent = `Chat with PDF: ${uploadedFileName || 'Conversation'}`;
      title.style.textAlign = 'center';
      title.style.marginBottom = '20px';
      title.style.color = '#333';
      pdfContent.appendChild(title);

      // Add timestamp
      const timestamp = document.createElement('p');
      timestamp.textContent = `Generated on: ${new Date().toISOString().split('T')[0]}`;
      timestamp.style.textAlign = 'center';
      timestamp.style.marginBottom = '30px';
      timestamp.style.color = '#666';
      pdfContent.appendChild(timestamp);

      // Add messages
      messages.forEach((message, index) => {
        if (index === 0 && message.type === 'assistant' && message.content === "Hello! I'm your AI assistant. Upload a PDF and ask me anything about it.") {
          // Skip the initial greeting
          return;
        }

        const messageDiv = document.createElement('div');
        messageDiv.style.marginBottom = '15px';
        messageDiv.style.padding = '10px';
        messageDiv.style.borderRadius = '5px';
        
        if (message.type === 'user') {
          messageDiv.style.backgroundColor = '#f0f7ff';
          messageDiv.style.marginLeft = '50px';
          messageDiv.innerHTML = `<strong>You:</strong> ${message.content}`;
        } else {
          messageDiv.style.backgroundColor = '#f9f9f9';
          messageDiv.style.marginRight = '50px';
          messageDiv.innerHTML = `<strong>AI Assistant:</strong> ${message.content}`;
        }
        
        pdfContent.appendChild(messageDiv);
      });

      // Add to DOM temporarily for rendering (off-screen)
      document.body.appendChild(pdfContent);
      
      // Wait for next animation frame to ensure the element is in the DOM
      await new Promise(resolve => requestAnimationFrame(resolve));
      
      // Add a small delay to ensure rendering is complete
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Wait for fonts to load if the API is available
      if (document.fonts && typeof document.fonts.ready === 'object') {
        await document.fonts.ready;
      }
      
      // Generate PDF
      const pdf = new jsPDF('p', 'pt', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      // Convert HTML to canvas - make sure the element is fully rendered
      const canvas = await html2canvas(pdfContent, {
        scale: 1.5, // Higher quality
        useCORS: true,
        logging: false,
        allowTaint: true,
        backgroundColor: '#ffffff',
        // Ensure we capture the full content
        windowWidth: chatWidth,
        windowHeight: 5000 // Large enough to capture all content
      });
      
      // Only remove the element after canvas generation is complete
      document.body.removeChild(pdfContent);
      
      // Calculate the number of pages
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = pdfWidth;
      const imgHeight = canvas.height * imgWidth / canvas.width;
      
      let heightLeft = imgHeight;
      let position = 0;
      
      // Add first page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;
      
      // Add subsequent pages if needed
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }
      
      // Save the PDF directly without showing any notification
      pdf.save(`${uploadedFileName || 'chat-conversation'}.pdf`);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      // Silent error handling - no UI notification
    }
  };

  // Handle sending a question to the API
  const handleSendQuestion = async () => {
    if (!question.trim() || !uploadedFileName || !uploadedFileId) return;
    
    // Add user question to messages
    const userQuestion = question.trim();
    setMessages(prevMessages => [
      ...prevMessages,
      { type: 'user', content: userQuestion }
    ]);
    
    // Clear the input and set loading state
    setQuestion('');
    setIsQuerying(true);
    
    try {
      const response = await fetch('https://api.temp2doc.com/api/query/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          filename: uploadedFileName,
          query: userQuestion
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Query failed with status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Add AI response to messages
      setMessages(prevMessages => [
        ...prevMessages,
        { 
          type: 'assistant', 
          content: data.answer || 'I couldn\'t find an answer to that question in the document.' 
        }
      ]);
    } catch (error) {
      console.error('Error querying PDF:', error);
      
      // Add error message
      setMessages(prevMessages => [
        ...prevMessages,
        { 
          type: 'assistant', 
          content: `Sorry, there was an error processing your question: ${error.message}` 
        }
      ]);
    } finally {
      setIsQuerying(false);
    }
  };
  
  // Handle file upload
  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadStatus({
        type: 'error',
        message: 'Please select a PDF file first.'
      });
      return;
    }
    
    setIsUploading(true);
    setUploadStatus({ type: 'info', message: 'Uploading file...' });
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await fetch('https://api.temp2doc.com/api/upload/', {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) {
        throw new Error(`Upload failed with status: ${response.status}`);
      }
      
      const data = await response.json();
      
      setUploadedFileId(data.id || data.file_id || 'success');
      setUploadedFileName(selectedFile.name);
      setUploadStatus({
        type: 'success',
        message: 'File uploaded successfully! You can now chat with your PDF.'
      });
      
      // Add a welcome message to the chat
      setMessages(prevMessages => [
        ...prevMessages,
        {
          type: 'assistant',
          content: `I've analyzed "${selectedFile.name}". What would you like to know about it?`
        }
      ]);
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadStatus({
        type: 'error',
        message: `Upload failed: ${error.message}`
      });
    } finally {
      setIsUploading(false);
    }
  };
  
  return (
    <>
      <SEOMetaTags 
        title="Chat with PDF using AI - Ask Questions & Get Answers | Temp2Doc"
        description="Upload your PDF and chat with it using our AI assistant. Get instant answers to your questions from any PDF document."
        keywords="chat with PDF, PDF assistant, AI PDF chat, document Q&A, PDF question answering"
        canonicalUrl="https://temp2doc.com/chat-with-pdf"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "Chat with PDF",
          "applicationCategory": "BusinessApplication",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "operatingSystem": "Web browser",
          "description": "Upload your PDF and chat with it using our AI assistant. Get instant answers to your questions from any PDF document."
        }}
      />
      <Header />
      <main className="min-h-screen bg-gradient-to-b from-white to-blue-50 pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <section className="max-w-6xl mx-auto text-center mb-12">
          <div className="inline-block p-3 bg-primary-100 rounded-full mb-4">
            <svg className="w-12 h-12 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
            </svg>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Chat with PDF using AI</h1>
          <p className="text-xl text-gray-600 mb-8">
            Upload your PDF and ask questions to get instant, intelligent answers from our AI assistant.
          </p>
        </section>

        {/* Upload Section */}
        <section className="max-w-6xl mx-auto mb-12">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="flex flex-col items-center mb-8">
          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Upload Your PDF</h2>
          <p className="text-gray-600 text-center max-w-md mb-6">
            Upload your PDF document to start chatting with it. Our AI will analyze the content and be ready to answer your questions.
          </p>
          <form className="w-full max-w-md">
            <div className="flex flex-col items-center">
              <label htmlFor="pdf-upload" className="w-full">
                <div className={`border-2 border-dashed border-gray-300 rounded-lg p-6 ${!uploadedFileId ? 'cursor-pointer hover:border-primary-500' : 'bg-gray-50'} transition-colors flex flex-col items-center ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  {uploadedFileId ? (
                    <svg className="w-12 h-12 text-green-500 mb-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="w-12 h-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                  )}
                  <span className="text-gray-600 mb-2">
                    {uploadedFileId ? `File uploaded: ${uploadedFileName}` : selectedFile ? selectedFile.name : 'Select PDF File'}
                  </span>
                  <span className="text-xs text-gray-500">
                    Max file size: 5MB
                  </span>
                </div>
              </label>
              <input
                id="pdf-upload"
                type="file"
                accept=".pdf"
                onChange={handleFileChange}
                className="hidden"
                disabled={isUploading || uploadedFileId}
              />
              
              {/* Upload button - only shown when not uploaded yet */}
              {!uploadedFileId && (
                <button
                  type="button"
                  onClick={handleUpload}
                  disabled={!selectedFile || isUploading}
                  className={`mt-4 w-full flex items-center justify-center px-4 py-3 rounded-lg text-white font-medium transition-colors ${!selectedFile || isUploading ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary-600 hover:bg-primary-700'}`}
                  aria-label="Upload PDF file"
                >
                  {isUploading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Uploading...
                    </>
                  ) : (
                    'Upload PDF'
                  )}
                </button>
              )}
              
              {/* Status message */}
              {uploadStatus.message && (
                <div role="alert" className={`mt-4 p-3 rounded-lg w-full ${
                  {
                    'error': 'bg-red-100 text-red-700',
                    'success': 'bg-green-100 text-green-700',
                    'warning': 'bg-yellow-100 text-yellow-700',
                    'info': 'bg-blue-100 text-blue-700',
                  }[uploadStatus.type] || 'bg-gray-100 text-gray-700'
                }`}>
                {uploadStatus.message}
              </div>
            )}
            </div>
          </form>
            </div>
          </div>
        </section>

        {/* Chat Interface Section */}
        <section className="max-w-6xl mx-auto mb-12">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Chat Interface</h2>
            <div ref={chatContainerRef} className="border border-gray-200 rounded-lg h-96 mb-6 overflow-y-auto p-4 bg-gray-50" role="log" aria-live="polite" aria-label="Chat conversation">
              <div className="flex flex-col space-y-4">
                {messages.map((message, index) => (
                  <article key={index} className={`flex items-start ${message.type === 'user' ? 'justify-end' : ''}`}>
                    {message.type === 'assistant' && (
                      <div className="flex-shrink-0 bg-primary-100 rounded-full p-2">
                        <svg className="w-6 h-6 text-primary-600" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 005 10a1 1 0 10-2 0 8 8 0 0016 0 1 1 0 10-2 0 5.986 5.986 0 00-.454 2.084A5 5 0 008 11z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                    <div className={`${message.type === 'user' ? 'bg-primary-500 text-white' : 'bg-white text-gray-800'} rounded-lg px-4 py-3 shadow-sm ${message.type === 'assistant' ? 'ml-3' : 'mr-2'} max-w-3xl`}>
                      <p className="whitespace-pre-wrap">{message.content}</p>
                    </div>
                    {message.type === 'user' && (
                      <div className="flex-shrink-0 bg-gray-200 rounded-full p-2">
                        <svg className="w-6 h-6 text-gray-600" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </article>
                ))}
                {isQuerying && (
                  <div className="flex items-start">
                    <div className="flex-shrink-0 bg-primary-100 rounded-full p-2">
                      <svg className="w-6 h-6 text-primary-600 animate-pulse" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 005 10a1 1 0 10-2 0 8 8 0 0016 0 1 1 0 10-2 0 5.986 5.986 0 00-.454 2.084A5 5 0 008 11z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3 bg-white rounded-lg px-4 py-3 shadow-sm">
                      <p className="text-gray-800">Thinking...</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <form className="flex" onSubmit={(e) => { e.preventDefault(); handleSendQuestion(); }}>
              <label htmlFor="chat-input" className="sr-only">Type your question</label>
              <input 
                id="chat-input"
                type="text" 
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendQuestion()}
                placeholder="Ask a question about your PDF..." 
                disabled={!uploadedFileId || isQuerying}
                className={`flex-grow px-4 py-3 rounded-l-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500  ${!uploadedFileId ? 'opacity-50 cursor-not-allowed' : ''}`}
              />
              <button 
                type="submit"
                disabled={!question.trim() || isQuerying || !uploadedFileId}
                className={`flex items-center justify-center px-4 py-2 rounded-r-lg ${!question.trim() || isQuerying || !uploadedFileId ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary-600 hover:bg-primary-700'} text-white`}
                aria-label="Send question"
              >
                {isQuerying ? (
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : 'Send'}
              </button>
            </form>
            
            {/* Download Conversation Button */}
            {messages.length > 1 && uploadedFileId && (
              <div className="mt-4 flex justify-center">
                <button
                  onClick={handleDownloadPDF}
                  className="flex items-center justify-center px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white transition-colors"
                  aria-label="Download conversation as PDF"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                  </svg>
                  Download Conversation as PDF
                </button>
              </div>
            )}
          </div>
        </section>

        {/* How It Works */}
        <section className="max-w-6xl mx-auto mb-12">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-center w-12 h-12 bg-primary-100 text-primary-600 rounded-full mb-4 font-bold text-lg">
                1
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Upload PDF</h3>
              <p className="text-gray-600 text-sm">Upload any PDF document to our secure platform.</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-center w-12 h-12 bg-primary-100 text-primary-600 rounded-full mb-4 font-bold text-lg">
                2
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">AI Analysis</h3>
              <p className="text-gray-600 text-sm">Our AI analyzes and understands the content of your document.</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-center w-12 h-12 bg-primary-100 text-primary-600 rounded-full mb-4 font-bold text-lg">
                3
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Ask Questions</h3>
              <p className="text-gray-600 text-sm">Ask questions and get instant, accurate answers based on your document.</p>
            </div>
          </div>
        </section>

        {/* Key Features */}
        <section className="max-w-6xl mx-auto mb-12">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">Key Features</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-primary-600 mb-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">AI-Powered Chat</h3>
              <p className="text-gray-600 text-sm">
                Interact with your PDF documents using advanced AI to get instant answers.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-primary-600 mb-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Smart Search</h3>
              <p className="text-gray-600 text-sm">
                Find information instantly without manually searching through pages.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-primary-600 mb-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Secure & Private</h3>
              <p className="text-gray-600 text-sm">
                Your files are processed securely and not stored on our servers.
              </p>
            </div>
          </div>
        </section>

        {/* Related Tools Section */}
        <section className="max-w-6xl mx-auto">
          <nav className="bg-white rounded-lg shadow-md p-6 border border-gray-200" aria-label="Related Tools">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">Related PDF Tools</h2>
            <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <li>
                <a href="/merge-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                  <span className="text-primary-600 font-medium">Merge PDF</span>
                </a>
              </li>
              <li>
                <a href="/split-remove-rotate-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                  <span className="text-primary-600 font-medium">Split Remove Rotate PDF</span>
                </a>
              </li>
              <li>
                <a href="/pdf-to-word" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
              <span className="text-primary-600 font-medium">PDF to Word</span>
            </a>
          </li>
              <li>
                <a href="/pdf-to-excel" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
              <span className="text-primary-600 font-medium">PDF to Excel</span>
            </a>
          </li>
              <li>
                <a href="/pdf-to-image" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
              <span className="text-primary-600 font-medium">PDF to Image</span>
            </a>
          </li>
              <li>
                <a href="/pdf-to-powerpoint" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
              <span className="text-primary-600 font-medium">PDF to PowerPoint</span>
            </a>
          </li>
              <li>
                <a href="/protect-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
              <span className="text-primary-600 font-medium">Protect PDF</span>
            </a>
          </li>
              <li>
                <a href="/unlock-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
              <span className="text-primary-600 font-medium">Unlock PDF</span>
            </a>
          </li>
              <li>
                <a href="/transform-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
              <span className="text-primary-600 font-medium">Repair & Transform PDF</span>
            </a>
          </li>
              <li>
                <a href="/compress-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
              <span className="text-primary-600 font-medium">Compress PDF</span>
            </a>
          </li>
              <li>
                <a href="/diagnose-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
              <span className="text-primary-600 font-medium">Verify & Diagnose PDF</span>
            </a>
          </li>
              <li>
                <a href="/" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
              <span className="text-primary-600 font-medium">Back to Home</span>
            </a>
          </li>
            </ul>
          </nav>
        </section>
      </main>
    </>
  );
};


export default ChatWithPdf;
